import React from 'react';
import { AlertTriangle, X, CheckCircle } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  icon?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = '确认',
  cancelText = '取消',
  type = 'danger',
  icon,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'danger':
        return <X className="w-6 h-6 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
      case 'info':
        return <CheckCircle className="w-6 h-6 text-blue-500" />;
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 text-white';
      case 'info':
        return 'bg-primary-600 hover:bg-primary-700 text-white';
    }
  };

  // 判断是否显示取消按钮
  const showCancelButton = cancelText && cancelText.trim() !== '';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* 遮罩层 */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" 
        onClick={showCancelButton ? onCancel : undefined}
      />

      {/* 对话框 */}
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all animate-fade-in">
        {/* 图标和标题 */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="flex-shrink-0 p-2 bg-gray-100 rounded-full">
            {getIcon()}
          </div>
          <div className="flex-1">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            )}
            <p className="text-gray-700 whitespace-pre-line">{message}</p>
          </div>
        </div>

        {/* 按钮 */}
        <div className={`flex ${showCancelButton ? 'justify-end space-x-3' : 'justify-center'} mt-6`}>
          {showCancelButton && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg transition-colors font-medium ${getConfirmButtonClass()}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
